/**
 * Run-Inspector — Glass-Box-Pattern Schicht B (Run-UUIDs).
 *
 * Während Step-UUIDs (deterministisch) die POSITION eines Schritts adressieren,
 * adressiert die Run-UUID die AUSFÜHRUNG: pg-boss-Job.id. Jeder Retry oder
 * Re-OCR generiert eine neue Run-UUID — so kann der User über die Zeit fragen:
 *
 *     "Run e91c0c5a hängt seit 8min in Queue, was los?"
 *
 * Der Agent lookup-t per SQL gegen pgboss.job + erklärt den State.
 *
 * Wird nur angezeigt wenn ein aktiver Job existiert (state IN active/retry/created).
 * Nach ocr_complete ist der Job gelöscht und das Panel verschwindet.
 */
import { useState, useEffect } from "react";
import type { Card } from "../../lib/api";
import { computeOcrProgress } from "../../lib/ocrProgress";

interface RunInspectorProps {
  card: Card;
  /** Average runtime seconds from QueueDashboard's /api/queue/status. */
  avgRuntimeS?: number | null;
}

export function RunInspector({ card, avgRuntimeS }: RunInspectorProps) {
  const [, forceTick] = useState(0);

  const jobId = card.ocr_queue_job_id;
  const queueState = card.ocr_queue_state;
  const queuedAt = card.ocr_queued_at;
  const startedAt = card.ocr_started_at;
  const retryCount = card.ocr_retry_count ?? 0;
  const queuePos = card.ocr_queue_position;

  // Tick every second for the duration counter
  useEffect(() => {
    if (!jobId) return;
    const t = setInterval(() => forceTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [jobId]);

  if (!jobId || !queueState) {
    // No active job — nothing to inspect
    return null;
  }

  const now = Date.now();
  const queuedMs = queuedAt ? now - new Date(queuedAt).getTime() : 0;
  const startedMs = startedAt ? now - new Date(startedAt).getTime() : null;

  return (
    <div className="run-inspector" style={{
      marginBottom: 14,
      padding: 14,
      background: "#fffbeb",
      border: "1px solid #fcd34d",
      borderRadius: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>Run-Inspector</strong>
        <span style={{
          padding: "2px 10px", borderRadius: 12,
          background: queueState === "active" ? "#3b82f6"
                    : queueState === "retry" ? "#dc2626"
                    : "#f59e0b",
          color: "white", fontSize: 11, fontWeight: 600,
        }}>
          {queueState === "created" ? "in Queue" : queueState}
        </span>
        <RunUuidChip jobId={jobId} />
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#6b7280" }}>
          pg-boss-Job · adressierbar als Run-UUID
        </span>
      </div>

      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px", fontSize: 13 }}>
        {queuedAt && (
          <>
            <strong>Enqueued</strong>
            <span>
              {new Date(queuedAt).toLocaleString("de-AT")}{" "}
              <span style={{ color: "#6b7280" }}>(vor {formatDur(queuedMs)})</span>
            </span>
          </>
        )}
        {queueState === "created" && (
          <>
            <strong>Queue-Position</strong>
            <span>
              {queuePos == null ? "?" : queuePos === 0 ? "Nächster" : `${queuePos} Jobs voraus`}
            </span>
            <strong>Wartezeit</strong>
            <span style={{ color: "#1d4ed8", fontWeight: 600 }}>⏱ {formatDur(queuedMs)}</span>
          </>
        )}
        {queueState === "active" && startedAt && (
          <>
            <strong>Worker started</strong>
            <span>
              {new Date(startedAt).toLocaleString("de-AT")}{" "}
              <span style={{ color: "#6b7280" }}>(seit {formatDur(startedMs!)})</span>
            </span>
            <strong>Worker-Laufzeit</strong>
            <span style={{ color: "#1d4ed8", fontWeight: 600 }}>⏱ {formatDur(startedMs!)}</span>
          </>
        )}
        {queueState === "retry" && (
          <>
            <strong>Retry-Status</strong>
            <span style={{ color: "#dc2626" }}>
              Versuch {retryCount + 1} läuft (vorherige fehlgeschlagen)
            </span>
          </>
        )}
        <strong>Retry-Count</strong>
        <span>{retryCount}{retryCount > 0 && <span style={{ color: "#dc2626" }}> ⚠</span>}</span>
      </div>

      {/* Progress vs avg runtime — honest representation, no 99% lie */}
      {queueState === "active" && startedMs != null && avgRuntimeS && avgRuntimeS > 0 && (
        <ProgressBar elapsedMs={startedMs} avgMs={avgRuntimeS * 1000} />
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280", borderTop: "1px solid #fcd34d", paddingTop: 6 }}>
        Adressierbar an den Agent: <code>Run {jobId.slice(0, 8)}</code> — der Agent kann pg-boss
        nach diesem Job fragen und State/Errors/Output erklären.
      </div>
    </div>
  );
}

// ─── Honest Progress Bar (no 99% cap, no lying) ───────────────────────────

function ProgressBar({ elapsedMs, avgMs }: { elapsedMs: number; avgMs: number }) {
  const { pct, overBudget, barPct } = computeOcrProgress(elapsedMs, avgMs);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
        <span style={{ color: "#374151", fontWeight: 600 }}>
          Fortschritt vs Ø Run-Time
        </span>
        <span style={{
          fontWeight: 600,
          color: overBudget ? "#dc2626" : pct > 80 ? "#d97706" : "#3b82f6",
        }}>
          {Math.floor(elapsedMs / 1000)}s{overBudget && " · länger als üblich"}
        </span>
      </div>
      <div style={{
        height: 8, borderRadius: 4,
        background: overBudget ? "#fee2e2" : "#e5e7eb",
        overflow: "hidden", position: "relative",
      }}>
        <div style={{
          width: `${barPct}%`, height: "100%",
          background: overBudget ? "#dc2626" : "#3b82f6",
          transition: "width 0.3s ease",
        }} />
        {overBudget && (
          <div style={{
            position: "absolute", top: 0, right: 0, height: "100%",
            width: "30%",
            background: "repeating-linear-gradient(45deg, #dc2626, #dc2626 4px, #991b1b 4px, #991b1b 8px)",
            opacity: 0.5,
          }} />
        )}
      </div>
      <div style={{ fontSize: 10, color: "#6b7280", marginTop: 3, display: "flex", justifyContent: "space-between" }}>
        <span>Elapsed {formatDur(elapsedMs)} / Ø {formatDur(avgMs)}</span>
        {overBudget && (
          <span style={{ color: "#dc2626" }}>
            Karte braucht länger als der Durchschnitt — OCR-Sidecar prüfen (docker stats sibit-ocr)
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Run-UUID Chip (volle UUID, separat von Step-UUIDs) ───────────────────

function RunUuidChip({ jobId }: { jobId: string }) {
  const [copied, setCopied] = useState(false);

  function copy(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(jobId).catch(() => {});
      } else {
        const ta = document.createElement("textarea");
        ta.value = jobId;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch { /* still flash */ }
    // Selektion explizit aufheben — siehe SettingsTestOcr StepUuidChip.copy.
    try { window.getSelection()?.removeAllRanges(); } catch { /* fine */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }

  return (
    <code
      onClick={copy}
      title={`Run-UUID: ${jobId}\nKlick zum Kopieren — diese UUID adressiert genau diese OCR-Ausführung. Bei Retry/Re-OCR neue UUID.`}
      style={{
        fontSize: 12, padding: "3px 9px", borderRadius: 12,
        background: copied ? "#10b981" : "#fef3c7",
        color: copied ? "white" : "#92400e",
        cursor: "pointer", fontFamily: "monospace", fontWeight: 600,
        userSelect: "none",
        border: copied ? "1px solid #10b981" : "1px solid #f59e0b",
        whiteSpace: "nowrap",
        display: "inline-flex", alignItems: "center", gap: 4,
      }}
    >
      <span style={{ fontSize: 10, opacity: 0.7 }}>{copied ? "✓" : "⧉"}</span>
      {copied ? "kopiert" : jobId.slice(0, 8)}
    </code>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatDur(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs.toString().padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}
